import { FC, useState } from 'react';
import { Modal, ModalFooter } from '@/components/ui';
import { Linkedin } from 'lucide-react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import axios from 'axios';

interface LinkedInConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect?: () => void; // Optional callback after initiating connection
  closable?: boolean; // If false, modal cannot be closed (no close button, no cancel, no escape)
}

/**
 * Modal shown when external user doesn't have LinkedIn token
 * Prompts user to connect their LinkedIn account for posting
 */
export const LinkedInConnectionModal: FC<LinkedInConnectionModalProps> = ({
  isOpen,
  onClose,
  onConnect,
  closable = true, // Default to true for backward compatibility
}) => {
  const [isConnecting, setIsConnecting] = useState(false);

  const handleLinkedInConnect = async () => {
    setIsConnecting(true);
    try {
      // Get OAuth URL from backend
      const response = await api.post('/auth/linkedin-connect-posting');
      const { authUrl } = response.data;

      // Call optional callback
      onConnect?.();

      // Redirect to LinkedIn OAuth
      window.location.href = authUrl;
    } catch (error: unknown) {
      console.error('Failed to initiate LinkedIn connection:', error);

      let errorMessage = 'Failed to connect LinkedIn. ';

      if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
        errorMessage += `Network error - cannot reach backend. Please check if the backend server is running on port 5000.`;
      } else {
        errorMessage += getErrorMessage(error);
      }

      alert(errorMessage);
      setIsConnecting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Connect LinkedIn Account"
      size="md"
      showCloseButton={closable}
      closeOnClickOutside={closable}
      closeOnEscape={closable}
    >
      <div className="space-y-4">
        <div className="flex items-center justify-center p-4 bg-blue-50 rounded-lg">
          <Linkedin className="w-12 h-12 text-[#0077b5]" />
        </div>

        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            LinkedIn Connection Required
          </h3>
          <p className="text-sm text-gray-600">
            To post content to your LinkedIn profile, you need to connect your LinkedIn account.
            This allows us to publish posts on your personal profile.
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <p className="text-sm font-semibold text-gray-900">What you'll get:</p>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>Post content directly to your LinkedIn profile</li>
            <li>Schedule posts for later</li>
            <li>Track engagement and analytics</li>
          </ul>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
          <p className="text-xs font-semibold text-blue-900">Required Permissions:</p>
          <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
            <li><strong>Profile & Email:</strong> To identify your account (required by LinkedIn's authentication system)</li>
            <li><strong>Post on your behalf:</strong> To publish content to your LinkedIn profile</li>
          </ul>
          <p className="text-xs text-blue-700 mt-2">
            <strong>Note:</strong> We post to your personal LinkedIn profile. Profile & Email permissions are required by LinkedIn's authentication system.
          </p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-xs text-yellow-800">
            <strong>Note:</strong> You'll be redirected to LinkedIn to authorize posting permissions.
            This is a secure OAuth flow managed by LinkedIn. You can revoke these permissions anytime in your LinkedIn settings.
          </p>
        </div>

        <ModalFooter align={closable ? "between" : "right"}>
          {closable && (
            <button
              onClick={onClose}
              disabled={isConnecting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleLinkedInConnect}
            disabled={isConnecting}
            className="px-4 py-2 text-sm font-medium text-white bg-[#0077b5] rounded-md hover:bg-[#005885] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0077b5] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isConnecting ? (
              <>
                <span className="animate-spin">⏳</span>
                <span>Connecting...</span>
              </>
            ) : (
              <>
                <Linkedin className="w-4 h-4" />
                <span>Connect LinkedIn</span>
              </>
            )}
          </button>
        </ModalFooter>
      </div>
    </Modal>
  );
};


